import { CloseIcon, EditIcon, HamburgerIcon, MoonIcon, SunIcon } from '@chakra-ui/icons'
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  Icon,
  IconButton,
  IconProps,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Portal,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useColorMode,
  useDisclosure,
  VStack,
} from '@chakra-ui/react'
import { RoundedButton } from '@components/Buttons'
import { useAuth, useScreen } from '@hb/react'
import {
  EncounterType,
  isSubsequentDayComplete,
  LaborEntryType,
  SubsequentDayNote,
} from '@hb/shared'
import { useCallback, useMemo, useState } from 'react'
import { LockIcon } from '../../../icons/LockIcon'
import { useLaborCharting } from './context'
import { EntriesTab } from './EntriesTab'
import { FullDeliveryRecordModal } from './FullDeliveryRecord'
import { GraphsTab } from './GraphsTab'
import { HomebirthActivationModal } from './HomebirthActivationModal'
import { LaborDocumentationPrompts } from './LaborDocumentationPrompts'
import { LaborEntryView } from './LaborEntry'
import { LaborTab } from './LaborEntry/types'
import { LaborTimeLogPanel } from './LaborTimeLogPanel'
import { MedsTab } from './MedsTab'
import { NeonatalRecordTab } from './NeonatalRecord'
import { NotesTab } from './NotesTab'
import { ReportBirthModal } from './ReportBirthModal'

// ── Tab icons ───────────────────────────────────────────────

const VitalsIcon = (props: IconProps) => (
  <Icon viewBox="0 -960 960 960" fill="currentColor" {...props}>
    <path d="M280-80v-200H80v-170h264l71 106q4 6 10 10t13.83 4q10.17 0 18.21-6 8.04-6 10.96-15l61-182 46 69q5 6 11 10t14 4h280v170H680v200H280Zm151-347-46-70q-3.79-5.78-10.61-9.39Q367.58-510 360-510H80v-170h200v-200h400v200h200v170H616l-71-106q-5-6-11-10t-13.71-4q-10.02 0-18.11 6-8.1 6-11.18 15l-60 182Z" />
  </Icon>
)

const MedsIcon = (props: IconProps) => (
  <Icon viewBox="0 -960 960 960" fill="currentColor" {...props}>
    <path d="M440-260h80v-120h120v-80H520v-120h-80v120H320v80h120v120ZM260-120q-24.75 0-42.37-17.63Q200-155.25 200-180v-479q0-24.75 17.63-42.38Q235.25-719 260-719h440q24.75 0 42.38 17.62Q760-683.75 760-659v479q0 24.75-17.62 42.37Q724.75-120 700-120H260Zm-20-660v-60h480v60H240Z" />
  </Icon>
)

const NotesIcon = (props: IconProps) => (
  <Icon viewBox="0 -960 960 960" fill="currentColor" {...props}>
    <path d="M120-240v-80h480v80H120Zm0-200v-80h720v80H120Zm0-200v-80h720v80H120Z" />
  </Icon>
)

const NeonatalIcon = (props: IconProps) => (
  <Icon viewBox="0 -960 960 960" fill="currentColor" {...props}>
    <path d="M480-80q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Zm0-80q134 0 227-93t93-227q0-134-93-227t-227-93q-134 0-227 93t-93 227q0 134 93 227t227 93Zm0-80q-100 0-170-70t-70-170q0-100 70-170t170-70q100 0 170 70t70 170q0 100-70 170t-170 70Zm0-80q66 0 113-47t47-113q0-66-47-113t-113-47q-66 0-113 47t-47 113q0 66 47 113t113 47Z" />
  </Icon>
)

// ── Entry types ─────────────────────────────────────────────

interface EntryTypeDef {
  id: LaborEntryType
  label: string
}

const LABOR_ENTRY_TYPES: EntryTypeDef[] = [
  { id: LaborEntryType.VITALS, label: 'Vitals' },
  { id: LaborEntryType.CERVICAL_EXAM, label: 'Cervical Exam' },
  { id: LaborEntryType.FHR, label: 'FHR' },
  { id: LaborEntryType.CONTRACTION, label: 'Contraction' },
  { id: LaborEntryType.MEDICATION, label: 'Medication' },
  { id: LaborEntryType.NARRATIVE, label: 'Narrative' },
  { id: LaborEntryType.PROCEDURE, label: 'Procedure' },
]

// ── Tab definitions ─────────────────────────────────────────

interface TabDef {
  id: LaborTab
  label: string
  icon: React.FC<IconProps>
}

const LABOR_TABS: TabDef[] = [
  { id: 'entries', label: 'Entries', icon: HamburgerIcon },
  { id: 'graphs', label: 'Graphs', icon: VitalsIcon },
  { id: 'meds', label: 'Meds', icon: MedsIcon },
  { id: 'notes', label: 'Notes', icon: NotesIcon },
]

const TabContent: Record<LaborTab, React.FC> = {
  entries: EntriesTab,
  graphs: GraphsTab,
  meds: MedsTab,
  notes: NotesTab,
  neonatal: NeonatalRecordTab,
}

const NEONATAL_TAB: TabDef = { id: 'neonatal', label: 'Neonatal', icon: NeonatalIcon }

// ── Shared header title/badges/Report-Birth button ──────────

const LaborHeaderCore = ({ mobileView, isActive }: { mobileView: boolean; isActive: boolean }) => {
  const { stage, reportBirthTapped, minimumDeliveryReport, fullDeliveryRecord } = useLaborCharting()
  const { isOpen: isReportOpen, onOpen: openReport, onClose: closeReport } = useDisclosure()
  const {
    isOpen: isDeliveryRecordOpen,
    onOpen: openDeliveryRecord,
    onClose: closeDeliveryRecord,
  } = useDisclosure()

  const handleReportBirth = () => {
    reportBirthTapped()
    openReport()
  }

  return (
    <Flex align="center" flex={1} minW={0} gap={2}>
      <Text
        fontFamily="Hero-New"
        fontWeight={700}
        fontSize="lg"
        color={isActive ? 'pink.700' : 'gray.500'}
        _dark={{ color: isActive ? 'pink.200' : 'gray.300' }}>
        LABOR
      </Text>
      {isActive && (
        <Badge ml={1} colorScheme="pink" fontFamily="Hero-New" fontSize="xs">
          Active
        </Badge>
      )}
      {stage === 'postpartum' && (
        <Badge ml={1} colorScheme="gray" fontFamily="Hero-New" fontSize="xs">
          History
        </Badge>
      )}

      {/* §4.1.1: Report Birth — prominently placed, always accessible during active labor */}
      {stage === 'labor' && !minimumDeliveryReport && (
        <Button
          ml="auto"
          size={mobileView ? 'sm' : 'md'}
          colorScheme="pink"
          fontFamily="Hero-New"
          fontWeight={700}
          boxShadow="md"
          onClick={handleReportBirth}>
          Report Birth
        </Button>
      )}
      {stage === 'labor' && minimumDeliveryReport && (
        <Button
          ml="auto"
          size={mobileView ? 'sm' : 'sm'}
          colorScheme="pink"
          variant="outline"
          fontFamily="Hero-New"
          onClick={handleReportBirth}>
          Birth Reported
        </Button>
      )}

      {/* §5.1: Full Delivery Record — accessible in postpartum stage */}
      {stage === 'postpartum' && (
        <Button
          ml="auto"
          size={mobileView ? 'sm' : 'sm'}
          colorScheme="pink"
          fontFamily="Hero-New"
          fontWeight={700}
          variant={fullDeliveryRecord?.savedAt ? 'outline' : 'solid'}
          onClick={openDeliveryRecord}>
          {fullDeliveryRecord?.savedAt ? 'View Delivery Record' : 'Complete Delivery Record'}
        </Button>
      )}

      <ReportBirthModal isOpen={isReportOpen} onClose={closeReport} />
      <FullDeliveryRecordModal isOpen={isDeliveryRecordOpen} onClose={closeDeliveryRecord} />
    </Flex>
  )
}

// ── Preview header (no controls) ────────────────────────────

const LaborPreviewHeader = ({
  isLocked,
  mobileView,
  isActive,
  onOpen,
}: {
  isLocked: boolean
  mobileView: boolean
  isActive: boolean
  onOpen: () => void
}) => (
  <Flex
    w="100%"
    align="center"
    px={4}
    py={3}
    gap={2}
    bg={isActive ? 'pink.50' : 'gray.50'}
    borderBottom="1px solid"
    borderColor={isActive ? 'pink.200' : 'gray.100'}
    shrink={0}
    _dark={{
      bg: isActive ? 'pink.900' : 'gray.800',
      borderBottomColor: isActive ? 'pink.700' : 'whiteAlpha.100',
    }}>
    <LaborHeaderCore mobileView={mobileView} isActive={isActive} />
    {!isLocked ? (
      <Button
        ml="auto"
        size="sm"
        colorScheme="pink"
        boxShadow="md"
        textShadow="md"
        fontFamily="Hero-New"
        onClick={onOpen}>
        View
      </Button>
    ) : null}
  </Flex>
)

// ── Modal header (dark/light toggle + close button) ──────────

const LaborModalHeader = ({
  mobileView,
  isActive,
  onClose,
}: {
  mobileView: boolean
  isActive: boolean
  onClose: () => void
}) => {
  const iconSize = mobileView ? ['4', '6'] : 5
  const { colorMode, toggleColorMode } = useColorMode()
  const { isOpen: isTimeLogOpen, onOpen: openTimeLog, onClose: closeTimeLog } = useDisclosure()

  return (
    <Flex
      w="100%"
      align="center"
      pl={mobileView ? [2, 3] : 2}
      pr={mobileView ? [2, 3] : 2}
      py={mobileView ? [1, 1.5] : 1}
      gap={mobileView ? [1, 1.5] : 2}
      bg={isActive ? 'pink.50' : 'gray.50'}
      borderBottom="1px solid"
      borderColor={isActive ? 'pink.200' : 'gray.100'}
      shrink={0}
      _dark={{
        bg: isActive ? 'pink.900' : 'gray.750',
        borderBottomColor: isActive ? 'pink.700' : 'whiteAlpha.100',
      }}>
      <LaborHeaderCore mobileView={mobileView} isActive={isActive} />
      {/* §4.3: Time Log button — accessible from the modal header */}
      <Button
        size={mobileView ? 'xs' : 'xs'}
        variant="ghost"
        colorScheme="pink"
        fontFamily="Hero-New"
        onClick={openTimeLog}>
        Time Log
      </Button>
      <LaborTimeLogPanel isOpen={isTimeLogOpen} onClose={closeTimeLog} />
      <IconButton
        ml={0}
        size={mobileView ? 'md' : 'sm'}
        aria-label={colorMode === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
        icon={
          colorMode === 'light' ? <MoonIcon boxSize={iconSize} /> : <SunIcon boxSize={iconSize} />
        }
        variant="ghost"
        onClick={toggleColorMode}
      />
      <IconButton
        size={mobileView ? 'md' : 'sm'}
        aria-label="Close labor view"
        icon={<CloseIcon boxSize={3} />}
        variant="ghost"
        onClick={onClose}
      />
    </Flex>
  )
}

// ── Entry type selector (mobile) ────────────────────────────

const EntryTypeSelector = () => {
  const { handleNewEntryType: handleSelectEntry, handleCancelEntry } = useLaborCharting()
  return (
    <Flex
      direction="column"
      w="100%"
      h="100%"
      p={4}
      gap={3}
      align="center"
      overflowY="auto"
      bg="#f7f7f7"
      _dark={{ bg: 'gray.900' }}>
      <Text
        fontSize={['lg', 'xl', '2xl']}
        py={[0, 1]}
        px={[2, 3]}
        fontWeight={600}
        fontFamily="Hero-New"
        textAlign="center"
        color="gray.600"
        _dark={{ color: 'gray.300' }}>
        New Entry
      </Text>
      {LABOR_ENTRY_TYPES.map(type => (
        <Button
          key={type.id}
          size={['md', 'lg', 'xl']}
          fontSize={['md', 'xl', '2xl']}
          py={[2, 3]}
          px={[2, 3]}
          maxW="500px"
          w="100%"
          variant="outline"
          colorScheme="pink"
          bg="white"
          _dark={{ bg: 'whiteAlpha.100' }}
          fontFamily="Hero-New"
          onClick={() => handleSelectEntry(type.id)}>
          {type.label}
        </Button>
      ))}
      <Button
        size={['md', 'lg', 'xl']}
        py={[2, 3]}
        variant="ghost"
        fontSize={['md', 'lg', 'xl']}
        fontFamily="Hero-New"
        color="gray.500"
        w="100%"
        maxW="500px"
        onClick={handleCancelEntry}>
        Cancel
      </Button>
    </Flex>
  )
}

// ── Mobile/tablet labor tab ─────────────────────────────────

const TabletLaborTab = ({
  activeTab,
  tab,
  onTabChange,
}: {
  activeTab: LaborTab
  tab: TabDef
  onTabChange: (tab: LaborTab) => void
}) => {
  const isActive = activeTab === tab.id
  const TabIcon = tab.icon
  return (
    <Flex
      key={tab.id}
      direction="row"
      align="center"
      justify="center"
      flex={1}
      minW="0"
      flexFlow={['column', 'row']}
      py={2}
      pl={3}
      pr={2}
      gap={[1, 1, 1.5]}
      cursor="pointer"
      bg={isActive ? 'pink.100' : 'blackAlpha.50'}
      onClick={() => onTabChange(tab.id)}
      borderRadius={6}
      transition="background 0.15s, border-color 0.15s"
      userSelect="none"
      _dark={{ bg: isActive ? 'pink.600' : 'whiteAlpha.200' }}>
      <Text
        fontSize={['md', 'lg', 'xl']}
        lineHeight={1}
        flex={1}
        minW={0}
        textAlign="left"
        fontWeight={isActive ? 700 : 500}
        fontFamily="Hero-New"
        color={isActive ? 'pink.600' : 'gray.500'}
        _dark={{ color: isActive ? 'pink.100' : 'gray.400' }}>
        {tab.label.toUpperCase()}
      </Text>
      <TabIcon
        boxSize={['6', '8', '10']}
        color={isActive ? 'pink.500' : 'gray.400'}
        _dark={{ color: isActive ? 'pink.100' : 'gray.400' }}
      />
    </Flex>
  )
}

// ── Mobile/tablet labor body ────────────────────────────────

const TabletLaborBody = ({ isActive }: { isActive: boolean }) => {
  const { isSelectingEntry, handleNewEntry, activeTab, setActiveTab, selectedEntry, stage } =
    useLaborCharting()

  const visibleTabs = useMemo(
    () => (stage === 'postpartum' ? [...LABOR_TABS, NEONATAL_TAB] : LABOR_TABS),
    [stage],
  )

  const Content = TabContent[activeTab] ?? TabContent.entries
  return (
    <Flex direction="column" w="100%" h="100%">
      <Box flex={1} minH={0} overflowY="auto" bg="#f7f7f7" _dark={{ bg: 'gray.900' }}>
        {isSelectingEntry ? (
          <EntryTypeSelector />
        ) : selectedEntry ? (
          <LaborEntryView selectedEntry={selectedEntry} />
        ) : (
          <Content />
        )}
      </Box>
      {isActive && !isSelectingEntry && !selectedEntry && (
        <Flex
          w="100%"
          borderTop="1px solid #cdcdcd"
          bg="white"
          px={2}
          py={1.5}
          shrink={0}
          _dark={{ bg: 'gray.800', borderTopColor: 'whiteAlpha.200' }}>
          <Button
            w="100%"
            colorScheme="pink"
            fontFamily="Hero-New"
            py={[2, 3]}
            size={['md', 'lg', 'xl']}
            fontSize={['md', 'xl', '2xl']}
            leftIcon={<EditIcon boxSize={[4, 5, 7]} />}
            onClick={handleNewEntry}>
            New Entry
          </Button>
        </Flex>
      )}
      {!selectedEntry && (
        <Flex
          as="nav"
          w="100%"
          borderTop="1px solid #cdcdcd"
          bg="white"
          justify="space-around"
          p={2}
          align="stretch"
          gap={2}
          shrink={0}
          _dark={{ bg: 'gray.800', borderTopColor: 'whiteAlpha.200' }}>
          {visibleTabs.map(tab => (
            <TabletLaborTab
              key={tab.id}
              activeTab={activeTab}
              tab={tab}
              onTabChange={setActiveTab}
            />
          ))}
        </Flex>
      )}
    </Flex>
  )
}

// ── Desktop labor view ──────────────────────────────────────

const laborTabToIndex = (tab: LaborTab, tabs: TabDef[]) => {
  const i = tabs.findIndex(t => t.id === tab)
  return i >= 0 ? i : 0
}

const DesktopLaborTab = ({ activeTab, tab }: { activeTab: LaborTab; tab: TabDef }) => {
  const isActive = activeTab === tab.id
  const TabIcon = tab.icon
  return (
    <Tab
      key={tab.id}
      py={1.5}
      px={3}
      gap={1.5}
      borderRadius="md"
      transition="background 0.15s, color 0.15s"
      fontFamily="Hero-New"
      _selected={{ bg: 'pink.100', _dark: { bg: 'pink.600' } }}>
      <Text
        fontSize="md"
        lineHeight={1}
        minW={0}
        textAlign="left"
        fontWeight={isActive ? 700 : 500}
        fontFamily="Hero-New"
        color={isActive ? 'pink.600' : 'gray.500'}
        _dark={{ color: isActive ? 'pink.100' : 'gray.400' }}>
        {tab.label}
      </Text>
      <TabIcon
        boxSize={['4', '5']}
        color={isActive ? 'pink.500' : 'gray.400'}
        _dark={{ color: isActive ? 'pink.100' : 'gray.400' }}
      />
    </Tab>
  )
}

const DesktopLaborContent = ({ isActive }: { isActive: boolean }) => {
  const {
    handleNewEntryType: handleSelectEntry,
    activeTab,
    setActiveTab,
    selectedEntry,
    stage,
  } = useLaborCharting()

  const visibleTabs = useMemo(
    () => (stage === 'postpartum' ? [...LABOR_TABS, NEONATAL_TAB] : LABOR_TABS),
    [stage],
  )

  if (selectedEntry) {
    return <LaborEntryView selectedEntry={selectedEntry} />
  }
  const Content = TabContent[activeTab] ?? TabContent.entries

  return (
    <Tabs
      index={laborTabToIndex(activeTab, visibleTabs)}
      onChange={i => {
        const next = visibleTabs[i]
        if (next) setActiveTab(next.id)
      }}
      size="sm"
      variant="unstyled"
      display="flex"
      flexDirection="column"
      h="100%"
      w="100%">
      <TabList
        flexShrink={0}
        borderBottom="1px solid #cdcdcd"
        bg="white"
        px={2}
        py={1.5}
        gap={1.5}
        _dark={{ bg: 'gray.800', borderBottomColor: 'whiteAlpha.200' }}>
        {visibleTabs.map(tab => (
          <DesktopLaborTab key={tab.id} activeTab={activeTab} tab={tab} />
        ))}
        {isActive && (
          <Menu placement="bottom-end">
            <MenuButton
              as={Button}
              ml="auto"
              size="sm"
              colorScheme="pink"
              fontFamily="Hero-New"
              leftIcon={<EditIcon />}>
              New Entry
            </MenuButton>
            <MenuList zIndex="popover">
              {LABOR_ENTRY_TYPES.map(type => (
                <MenuItem
                  key={type.id}
                  fontFamily="Hero-New"
                  onClick={() => handleSelectEntry(type.id)}>
                  {type.label}
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
        )}
      </TabList>
      <TabPanels flex={1} minH={0} display="flex" flexDirection="column" overflow="hidden">
        {visibleTabs.map(tab => (
          <TabPanel
            key={tab.id}
            flex={1}
            minH={0}
            p={0}
            overflowY="auto"
            bg="#f7f7f7"
            _dark={{ bg: 'gray.900' }}>
            <Content />
          </TabPanel>
        ))}
      </TabPanels>
    </Tabs>
  )
}

// ── §6.5: Subsequent-day documentation banner ───────────────

const SubsequentDayBanner = ({
  date,
  existing,
  onConfirm,
}: {
  date: string
  existing: SubsequentDayNote | undefined
  onConfirm: (note: SubsequentDayNote) => Promise<void>
}) => {
  const { authUser } = useAuth()
  const isComplete = isSubsequentDayComplete(existing)

  const [vitals, setVitals] = useState(existing?.vitalsConfirmed ?? false)
  const [fhr, setFhr] = useState(existing?.fhrConfirmed ?? false)
  const [timeLog, setTimeLog] = useState(existing?.timeLogConfirmed ?? false)
  const [saving, setSaving] = useState(false)

  const allChecked = vitals && fhr && timeLog

  const handleConfirm = useCallback(async () => {
    setSaving(true)
    await onConfirm({
      vitalsConfirmed: vitals,
      fhrConfirmed: fhr,
      timeLogConfirmed: timeLog,
      confirmedOn: Date.now(),
      confirmedBy: authUser?.uid ?? 'unknown',
    })
    setSaving(false)
  }, [onConfirm, vitals, fhr, timeLog, authUser])

  if (isComplete) return null

  const displayDate = new Date(date + 'T12:00:00').toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  })

  return (
    <Alert
      status="warning"
      borderRadius={4}
      fontSize="sm"
      alignItems="flex-start"
      flexDirection="column"
      py={2}
      px={3}>
      <Flex align="center" gap={1} mb={1}>
        <AlertIcon mt={0} />
        <Text fontWeight={600} fontSize="sm">
          Labor continued into {displayDate}
        </Text>
      </Flex>
      <Text fontSize="xs" color="orange.700" mb={2}>
        Confirm documentation for {displayDate} to support subsequent-day billing.
      </Text>
      <VStack align="flex-start" spacing={1} mb={2}>
        <Checkbox
          size="sm"
          colorScheme="orange"
          isChecked={vitals}
          onChange={e => setVitals(e.target.checked)}>
          <Text fontSize="xs">Maternal vitals documented</Text>
        </Checkbox>
        <Checkbox
          size="sm"
          colorScheme="orange"
          isChecked={fhr}
          onChange={e => setFhr(e.target.checked)}>
          <Text fontSize="xs">FHR documented</Text>
        </Checkbox>
        <Checkbox
          size="sm"
          colorScheme="orange"
          isChecked={timeLog}
          onChange={e => setTimeLog(e.target.checked)}>
          <Text fontSize="xs">Time log for this calendar day entered</Text>
        </Checkbox>
      </VStack>
      <Button
        size="xs"
        colorScheme="orange"
        isLoading={saving}
        isDisabled={!allChecked}
        onClick={handleConfirm}>
        Confirm documentation for {displayDate}
      </Button>
    </Alert>
  )
}

// ── Helpers ─────────────────────────────────────────────────

const formatDateTime = (ts: number) =>
  new Date(ts).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })

const ENTRY_TYPE_LABELS: Record<LaborEntryType, string> = {
  [LaborEntryType.VITALS]: 'Vitals',
  [LaborEntryType.CERVICAL_EXAM]: 'Cervical Exam',
  [LaborEntryType.FHR]: 'FHR',
  [LaborEntryType.CONTRACTION]: 'Contraction',
  [LaborEntryType.MEDICATION]: 'Medication',
  [LaborEntryType.NARRATIVE]: 'Narrative',
  [LaborEntryType.PROCEDURE]: 'Procedure',
}

// ── §6.5: In-modal new-calendar-day trigger ─────────────────
// Shown inside the charting modal (not in the preview card) whenever labor
// has crossed midnight and the midwife has not yet confirmed documentation
// for those calendar days. The preview-card SubsequentDayBanner handles the
// confirmation checkboxes; this strip is the initial in-charting prompt.

const NewCalendarDayStrip = () => {
  const { stage, laborCalendarDates, subsequentDayNotes, handleNewEntryType } = useLaborCharting()

  const pendingDates = useMemo(() => {
    if (stage !== 'labor') return []
    return laborCalendarDates
      .slice(1)
      .filter(date => !isSubsequentDayComplete(subsequentDayNotes[date]))
  }, [stage, laborCalendarDates, subsequentDayNotes])

  if (pendingDates.length === 0) return null

  const formatDate = (date: string) =>
    new Date(date + 'T12:00:00').toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
    })

  const latestPending = pendingDates[pendingDates.length - 1]

  return (
    <Alert
      status="warning"
      borderRadius={0}
      flexShrink={0}
      alignItems="flex-start"
      px={[3, 4]}
      py={2.5}
      gap={2}>
      <AlertIcon mt={0.5} flexShrink={0} />
      <Box flex={1} minW={0}>
        <Text fontWeight={700} fontSize="sm" fontFamily="Hero-New" mb={0.5}>
          New Calendar Day — Labor Continues
        </Text>
        <Text fontSize="xs" color="orange.700" _dark={{ color: 'orange.200' }}>
          {pendingDates.length === 1
            ? `Labor extended past midnight into ${formatDate(latestPending)}.`
            : `Labor spanned ${pendingDates.length} additional calendar days (through ${formatDate(latestPending)}).`}{' '}
          Document a progress note, maternal vitals, FHR, and time log for{' '}
          {pendingDates.length === 1 ? 'this calendar day' : 'each calendar day'} to support
          billing.
        </Text>
      </Box>
      <Button
        size="xs"
        colorScheme="orange"
        flexShrink={0}
        alignSelf="center"
        onClick={() => handleNewEntryType(LaborEntryType.NARRATIVE)}>
        Log Progress Note
      </Button>
    </Alert>
  )
}

// ── Full labor content (used inside modal) ───────────────────

const LaborFullContent = ({ mobileView, isActive }: { mobileView: boolean; isActive: boolean }) => (
  <Flex flex={1} minH={0} direction="column" overflow="hidden">
    <NewCalendarDayStrip />
    {/* §4.2: Non-blocking documentation prompts — only shown during active labor */}
    {isActive && <LaborDocumentationPrompts />}
    <Box flex={1} minH={0}>
      {mobileView ? (
        <TabletLaborBody isActive={isActive} />
      ) : (
        <DesktopLaborContent isActive={isActive} />
      )}
    </Box>
  </Flex>
)

// ── Labor section preview card ───────────────────────────────

export const LaborSectionPreview = ({ mobileView }: { mobileView: boolean }) => {
  const {
    stage,
    admission,
    deliveredOn,
    laborEntries,
    laborCalendarDates,
    subsequentDayNotes,
    confirmSubsequentDay,
    setOpenExam,
  } = useLaborCharting()
  const { height } = useScreen()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const {
    isOpen: isHomebirthOpen,
    onOpen: onHomebirthOpen,
    onClose: onHomebirthClose,
  } = useDisclosure()

  // §6.5: Day 2+ banners — only show during active labor or postpartum.
  const subsequentDates = useMemo(
    () => (stage !== 'prenatal' ? laborCalendarDates.slice(1) : []),
    [stage, laborCalendarDates],
  )

  const isActive = stage === 'labor'
  const isLocked = stage === 'prenatal'

  const entryCounts = useMemo(() => {
    const counts: Partial<Record<LaborEntryType, number>> = {}
    for (const entry of laborEntries) {
      counts[entry.type] = (counts[entry.type] ?? 0) + 1
    }
    return counts
  }, [laborEntries])

  const entrySummary = useMemo(
    () =>
      (Object.entries(entryCounts) as [LaborEntryType, number][])
        .map(([type, count]) => `${count} ${ENTRY_TYPE_LABELS[type]}`)
        .join(' · '),
    [entryCounts],
  )

  const notesPreview = useMemo(() => {
    const narratives = laborEntries
      .filter(e => e.type === LaborEntryType.NARRATIVE)
      .sort((a, b) => a.startedAt - b.startedAt)
    return narratives
      .map(e => (e.data as { noteBody?: string }).noteBody ?? '')
      .filter(Boolean)
      .join('\n\n')
  }, [laborEntries])

  return (
    <>
      <Box
        w="100%"
        bg="white"
        _dark={{ bg: 'gray.900' }}
        borderRadius={8}
        boxShadow="0 1px 4px #00000022"
        overflow="hidden"
        opacity={isLocked ? 0.65 : 1}>
        <LaborPreviewHeader
          isLocked={isLocked}
          mobileView={mobileView}
          isActive={isActive}
          onOpen={onOpen}
        />
        {isLocked ? (
          <Flex direction="column" gap={3} px={4} py={5}>
            <Flex align="center" gap={3} color="gray.400" _dark={{ color: 'gray.500' }}>
              <LockIcon />
              <Text fontFamily="Hero-New" fontSize="sm" fontWeight={600}>
                Labor &amp; Delivery — Not Yet Active
              </Text>
            </Flex>
            <Flex gap={2} flexWrap="wrap">
              <RoundedButton
                size="sm"
                colorScheme="pink"
                variant="outline"
                borderRadius={6}
                fontFamily="Hero-New"
                onClick={() =>
                  setOpenExam({
                    type: EncounterType.BIRTHING_CENTER_ADMISSION,
                    examId: null,
                    config: null,
                  })
                }>
                Submit Birthing Center Admission
              </RoundedButton>
              <Button
                size="sm"
                colorScheme="pink"
                variant="ghost"
                fontFamily="Hero-New"
                fontWeight={500}
                onClick={onHomebirthOpen}>
                Open Homebirth Episode
              </Button>
            </Flex>
            <HomebirthActivationModal isOpen={isHomebirthOpen} onClose={onHomebirthClose} />
          </Flex>
        ) : (
          <Flex direction="column" gap={2} px={4} py={3}>
            {/* Time range */}
            {admission && (
              <Flex align="center" gap={2} flexWrap="wrap">
                <Text
                  fontFamily="Hero-New"
                  fontSize="sm"
                  color="gray.600"
                  _dark={{ color: 'gray.400' }}>
                  <Text as="span" fontWeight={600}>
                    {'Admitted:'}
                  </Text>{' '}
                  {formatDateTime(admission.admittedAt)}
                </Text>
                {deliveredOn && (
                  <>
                    <Text color="gray.400" fontSize="sm">
                      {'→'}
                    </Text>
                    <Text
                      fontFamily="Hero-New"
                      fontSize="sm"
                      color="gray.600"
                      _dark={{ color: 'gray.400' }}>
                      <Text as="span" fontWeight={600}>
                        {'Delivered:'}
                      </Text>{' '}
                      {formatDateTime(deliveredOn)}
                    </Text>
                  </>
                )}
              </Flex>
            )}

            {/* Entry counts */}
            {entrySummary ? (
              <Text
                fontFamily="Hero-New"
                fontSize="sm"
                color="gray.500"
                _dark={{ color: 'gray.400' }}>
                {laborEntries.length} {laborEntries.length === 1 ? 'entry' : 'entries'}:{' '}
                {entrySummary}
              </Text>
            ) : (
              <Text
                fontFamily="Hero-New"
                fontSize="sm"
                fontWeight={500}
                color="gray.600"
                _dark={{ color: 'gray.400' }}
                fontStyle="italic">
                No entries yet
              </Text>
            )}

            {/* Notes preview */}
            {notesPreview && (
              <Text
                fontFamily="Hero-New"
                fontSize="sm"
                color="gray.600"
                _dark={{ color: 'gray.400' }}
                noOfLines={4}
                whiteSpace="pre-wrap">
                {notesPreview}
              </Text>
            )}

            {/* §6.5: Subsequent-day documentation banners */}
            {subsequentDates.map(date => (
              <SubsequentDayBanner
                key={date}
                date={date}
                existing={subsequentDayNotes[date]}
                onConfirm={note => confirmSubsequentDay(date, note)}
              />
            ))}
          </Flex>
        )}
      </Box>

      {/* Full-screen labor modal */}
      <Portal>
        <Modal isOpen={isOpen} onClose={onClose} size="full">
          <ModalOverlay />
          <ModalContent borderRadius={0} m={0}>
            <ModalBody p={0} display="flex" flexDirection="column" h="100vh">
              <Flex
                direction="column"
                w="100%"
                h={`${height}px`}
                bg="white"
                _dark={{ bg: 'gray.900' }}>
                <LaborModalHeader mobileView={mobileView} isActive={isActive} onClose={onClose} />
                <LaborFullContent mobileView={mobileView} isActive={isActive} />
              </Flex>
            </ModalBody>
          </ModalContent>
        </Modal>
      </Portal>
    </>
  )
}
